import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store.js";
import { lazy, useState, useEffect, useRef, Suspense } from "react";
import Header from "./components/header/header";
import Footer from "./components/footer/footer";
import ScrollToTop from "./components/scrollTop";
import LoadingBar from "react-top-loading-bar";
import PageNotFound from "./components/PageNotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import Preloader from "./Preloader";
import { PreloaderProvider } from "./PreloaderContext";
import "../src/assets/scss/main-style.scss";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { partnerDisplay } from "./components/constants/partnerList.js";
import { jwtDecode } from "jwt-decode";
import { isTokenExpired } from "./service/helpers";
import axios from "axios";
import { callToLoadAnalytics } from "./service/reports.js";
import { fetchLogin } from "./redux/slices/login.js";
import AnalyticsProvider from "./components/AnalyticsProvider";
import { analytics } from "./analytics.js";

const Home = lazy(() => import("./Pages/Home/Vehiclehistory"));
const VHR_viewFullReports = lazy(() =>
  import("./components/vehiclehistory/VHR_viewFullReports")
);

// Standalone Screens
const SubscriptionBenefits = lazy(() =>
  import("./components/vehiclehistory/subscription-benefits")
);
const VehicleHistoryReports = lazy(() =>
  import("./components/vehiclehistory/vehicle-history-reports")
);
const SampleVehicleHistoryReports = lazy(() =>
  import("./components/vehiclehistory/sample-vehicle-history-reports")
);
const SearchByLicensePlate = lazy(() =>
  import("./components/vehiclehistory/search-by-license-plate")
);
const AutoshoppersGuide = lazy(() =>
  import("./components/vehiclehistory/autoshoppers-guide")
);
const VinBasics = lazy(() => import("./components/vehiclehistory/vin-basics"));
const VinBasicsDecode = lazy(() =>
  import("./components/vehiclehistory/vin-basics")
);
const VinBasicsFindVin = lazy(() =>
  import("./components/vehiclehistory/vin-basics")
);
const OdometerFraud = lazy(() =>
  import("./components/vehiclehistory/odometer-fraud")
);
const CallForAction = lazy(() =>
  import("./components/vehiclehistory/call-for-action")
);
const TitleBrands = lazy(() =>
  import("./components/vehiclehistory/title-brands")
);
const Faq = lazy(() => import("./components/vehiclehistory/faq"));
const Contact = lazy(() => import("./components/vehiclehistory/contact"));
const Dispute = lazy(() => import("./components/vehiclehistory/dispute"));
const Checkout = lazy(() => import("./components/vehiclehistory/checkout"));
const FreeOnlineDelivery = lazy(() =>
  import("./components/vehiclehistory/free-online-delivery")
);
const TermsOfUse = lazy(() => import("./components/TermsOfUse"));
const SecureShopping = lazy(() =>
  import("./components/vehiclehistory/secure-shopping")
);
const Glossary = lazy(() => import("./components/vehiclehistory/glossary"));
const AutoCheckScore = lazy(() =>
  import("./components/vehiclehistory/autocheck-score")
);
const PatentNotice = lazy(() =>
  import("./components/vehiclehistory/patent-notice")
);
const VehicleBuybackProtection = lazy(() =>
  import("./components/vehiclehistory/vehicle-buyback-protection")
);
const BackedByExperian = lazy(() =>
  import("./components/vehiclehistory/backed-by-experian")
);
const AutoCheckVsCarfax = lazy(() =>
  import("./components/vehiclehistory/autocheck-vs-carfax")
);
const AutoCheckVsBargain = lazy(() =>
  import("./components/vehiclehistory/autocheck-vs-bargain")
);
const FloodRiskCheck = lazy(() =>
  import("./components/vehiclehistory/flood-risk-check")
);
const AffiliateProgram = lazy(() =>
  import("./components/vehiclehistory/affiliate-program")
);
const Login = lazy(() => import("./components/vehiclehistory/login"));
const BuybackTerms = lazy(() =>
  import("./components/vehiclehistory/buyback-terms")
);
const CarGurusUser = lazy(() =>
  import("./components/vehiclehistory/car-gurus-user")
);

const CarsUser = lazy(() => import("./components/vehiclehistory/cars-user"));
const FreeScore = lazy(() => import("./components/vehiclehistory/free-score"));
const MYVehicleOffers = lazy(() =>
  import("./components/vehiclehistory/my-vehicle-offers")
);
const PrefrencesUpdated = lazy(() => import("./components/PrefrencesUpdated"));

const RecallCheck = lazy(() =>
  import("./components/vehiclehistory/recall-check")
);

const CarAdvise = lazy(() => import("./components/vehiclehistory/car-advise"));

const WhyBuyvhr = lazy(() => import("./components/vehiclehistory/why-buy-vhr"));

const TrueCar8900LandingPage = lazy(() => import("./Pages/TrueCar/TrueCar8900"));

const PaymentGateway = lazy(() =>
  import("./components/vehiclehistory/payment-gateway")
);

const MyAutocheckDashboard = lazy(() =>
  import("./components/mydashboard/my-autocheck-dashboard")
);
const MyReportTab = lazy(() =>
  import("./components/mydashboard/my-report-tab")
);
const MyProfileTab = lazy(() =>
  import("./components/mydashboard/my-profile-tab")
);
const RegisterBuybackTab = lazy(() =>
  import("./components/mydashboard/register-for-buyback-protection-tab")
);
const UpdatePassword = lazy(() =>
  import("./components/mydashboard/update-password")
);

const YourAdChoices = lazy(() => import("./components/yourAdChoices"));

function AppContent() {
  const [progress, setProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { partnerSiteId } = useSelector((state) => state.authentication);
  const initializedRef = useRef(false);
   const analyticsLoadedRef = useRef(false);

  useEffect(() => {
    // Check URL for siteID parameter (case-insensitive)  
    const urlParams = new URLSearchParams(location.search);
    const urlSiteId = urlParams.get("siteID") || urlParams.get("siteid") || urlParams.get("siteId") || urlParams.get("SITEID");
    
    console.log('🔍 App.js - URL siteID:', urlSiteId);
    console.log('🔍 App.js - partnerSiteId from Redux:', partnerSiteId);
    console.log('🔍 App.js - initializedRef:', initializedRef.current);
    
    // Only dispatch API call if siteID is explicitly provided in URL
    if (urlSiteId) {
      console.log('✅ Dispatching with URL siteID:', urlSiteId);
      dispatch(
        fetchLogin({
          condition: "partnerSiteId",
          payload: parseInt(urlSiteId),
        })
      );
      initializedRef.current = true;
    } else if (!initializedRef.current && (!partnerSiteId || !partnerSiteId?.uid)) {
      // If not yet initialized and Redux state is empty or has no uid, check sessionStorage and populate Redux
      const storedPartnerSiteId = JSON.parse(sessionStorage.getItem("partnerSiteId"));
      console.log('📦 SessionStorage partnerSiteId:', storedPartnerSiteId);
      if (storedPartnerSiteId?.siteId !== undefined) {
        console.log('✅ Dispatching with sessionStorage siteID:', storedPartnerSiteId.siteId);
        dispatch(
          fetchLogin({
            condition: "partnerSiteId",
            payload: storedPartnerSiteId.siteId,
          })
        );
        initializedRef.current = true;
      }
    } else {
      console.log('ℹ️ Skipping dispatch - Redux already has data or initialized');
    }
    // If Redux state already has data and no URL siteID, do nothing - rely on existing state
  }, [location.search, dispatch]);

  // Load analytics configuration (ONCE per session)
  useEffect(() => {
      if (analyticsLoadedRef.current) {
      console.debug('⏭️ Analytics already loaded, skipping (saved reCAPTCHA call)');
      return;
    }

    analyticsLoadedRef.current = true;

    // CRITICAL FIX: Conditionally load analytics based on response (bot detection)
    // Matches legacy Vue.js behavior EXACTLY
    callToLoadAnalytics()
       .then((response) => {
        // response === true means reCAPTCHA challenge was passed (not a bot)
        if (response === true) {
          console.log('✅ Analytics authorized - initializing analytics (user verified)');
          // MATCHES LEGACY: loadAnalytics() - Initialize analytics ONLY after bot check passes
          analytics.initialize();
        } else {
          console.warn('⚠️ Analytics NOT loaded - bot detected or unauthorized:', response);
          // Block analytics from loading
          window.__DISABLE_ANALYTICS = true;
        }
      })
      .catch((error) => {
        console.warn("⚠️ Analytics configuration failed to load:", error);
         // MATCHES LEGACY: Fallback - Load analytics anyway on error (fail-open)
        console.log('📊 Loading analytics as fallback despite error');
        analytics.initialize();
        analyticsLoadedRef.current = false; // Reset on error to allow retry
      });
  }, []);

  useEffect(() => {
    setProgress(30);
    setTimeout(() => setProgress(100), 500);
  }, [location]);

  const titleMap = {
    "/vehiclehistory/": "AutoCheck.com | Homepage | AutoCheck.com",
    "/vehiclehistory/sample-vehicle-history-report":
      "Sample VehicleHistory Report | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/autoshoppers-guide":
      "How to Buy a Used Car - An Auto Shopper's Guide | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/backed-by-experian":
      "Data Backed by Experian | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/autocheck-score":
      "AutoCheck Score | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/vehicle-buyback-protection":
      "Buyback Protection | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/subscription-benefits":
      "SubscriptionBenefits | AutoCheck.com",
    "/vehiclehistory/flood-risk-check":
      "AutoCheck Flood Risk Check | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/dispute": "Contact | AutoCheck.com",
    "/vehiclehistory/contact": "Contact | AutoCheck.com",
    "/vehiclehistory/login": "Login | AutoCheck.com",
    "/vehiclehistory/search-by-license-plate":
      "Vehicle History Report by License Plate Number | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/vin-basics":
      "What is a VIN (Vehicle Identification Number)?| AutoCheck | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/odometer-fraud":
      "Car Odometer Rollback Fraud | AutoCheck | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/call-for-action":
      "Consumer Advocacy: Our partnership with Call for Action | AutoCheck.com",
    "/vehiclehistory/title-brands":
      "Car Branded Titles: Salvage, Lemon, Odometer Rollback| AutoCheck | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/faq":
      "Frequently Asked Questions | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/free-online-delivery":
      "Free Online Vehicle History Report Delivery | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/secure-shopping":
      "Secure Shopping | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/buyback-terms":
      "Buyback Terms | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/glossary":
      "Glossary for Autoshoppers | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/patent-notice":
      "Patent Notice | AutoCheck.com | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/autocheck-vs-carfax":
      "AutoCheck vs. Carfax | AutoCheck.com | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/autocheck-vs-bargain":
      "AutoCheck v Bargain Reports | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/affiliate-program":
      "Become Affiliate | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/dashboard": "Dashboard | AutoCheck.com",
    "/vehiclehistory/dashboard/my-profile-tab": "Profile | AutoCheck.com",
    "/vehiclehistory/vehicle-history-reports":
      "AutoCheck vehicle history reports | AutoCheck.com",
    "/vehiclehistory/your-ad-choices": "Your Ad Choices | AutoCheck.com",
    "/vehiclehistory/checkout": "Checkout | AutoCheck.com",
    "/vehiclehistory/car-gurus":
      "CarGurus Landing Page | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/free-score":
      "Free Vehicle History Report Score | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/my-vehicle-offers":
      "My Vehicle Offers | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/recall-check": "Does My Vehicle Have Any Recall Notices?",
    "/vehiclehistory/car-advise": "CarAdvise | AutoCheck.com | AutoCheck.com",
    "/vehiclehistory/truecar": "TrueCar Special Offer | AutoCheck.com",
    "/vehiclehistory/truecar8900": "TrueCar Special Offer | AutoCheck.com",
  };

  useEffect(() => {
    if (location.pathname.startsWith("/vehiclehistory/full-vehicle-report/")) {
      document.title = "Full Vehicle Report | AutoCheck.com";
      return;
    }

    // Check static routes
    const newTitle = titleMap[location.pathname];
    if (newTitle) {
      document.title = newTitle;
    }
  }, [location.pathname]);

  const handleLoaderFinished = () => {
    setProgress(0);
  };

  // const isTokenExpired = () => {
  //   const token = localStorage.getItem("consumerSite_token");
  //   if (token) {
  //     const { exp } = jwtDecode(token);
  //     const expirationTime = exp * 1000 - 60000;
  //     const currentTime = Date.now();
  //     return currentTime >= expirationTime;
  //   } else {
  //     return false;
  //   }
  // };

  useEffect(() => {
    if (isTokenExpired() === true) {
      localStorage.clear();
      navigate("/vehiclehistory/login", {});
    }
  }, [window.location.href]);

  return (
    <>
      <LoadingBar
        color="#004990"
        progress={progress}
        onLoaderFinished={handleLoaderFinished}
      />
      <Header />
      {location.pathname !== "/vehiclehistory/my-vehicle-offers" &&
        partnerDisplay?.map((val) => {
          if (val.headerPanel !== undefined) {
            if (val?.id === partnerSiteId?.uid) {
              return (
                <div className="container" key={val.id}>
                  <div className="row">
                    <div className="col-12 mt-3 mb-3">
                      <div className="row d-flex align-items-center partnerHeaderPanel">
                        <div className="partnerName col-7">
                          {val.headerPanel !== undefined && (
                            <>
                              <h3>
                                Welcome to AutoCheck
                                <sup>®</sup>
                              </h3>
                            </>
                          )}

                          <span>{val?.headerPanel?.title} </span>
                        </div>

                        {/* Render the logo only if it exists */}
                        {val?.headerPanel?.logo && (
                          <div className="partnerLogo col-5">
                            <img
                              src={val?.headerPanel?.logo}
                              alt="Partner logo"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          }

          return null;
        })}
      <ScrollToTop />
      <main>
        <Suspense fallback={<Preloader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/vehiclehistory/" element={<Home />} />
            <Route path="/vehiclehistory/truecar" element={<TrueCar8900LandingPage />} />
            {/* <Route path="/vehiclehistory" element={<Home />} /> */}
            <Route path="*" element={<PageNotFound />} />
            <Route path="/vehiclehistory/login" element={<Login />} />
            <Route
              path="/vehiclehistory/subscription-benefits"
              element={<SubscriptionBenefits />}
            />
            <Route
              path="/vehiclehistory/search-by-license-plate"
              element={<SearchByLicensePlate />}
            />
            <Route
              path="/vehiclehistory/terms-of-use"
              element={<TermsOfUse />}
            />

            <Route
              path="/vehiclehistory/vehicle-history-reports"
              element={<VehicleHistoryReports />}
            />
            <Route
              path="/vehiclehistory/sample-vehicle-history-report"
              element={<SampleVehicleHistoryReports />}
            />
            <Route
              path="/vehiclehistory/autoshoppers-guide"
              element={<AutoshoppersGuide />}
            />
            <Route path="/vehiclehistory/vin-basics" element={<VinBasics />} />
            <Route
              path="/vehiclehistory/vin-basics#decode"
              element={<VinBasicsDecode />}
            />
            <Route
              path="/vehiclehistory/vin-basics#findvin"
              element={<VinBasicsFindVin />}
            />
            <Route
              path="vehiclehistory/full-vehicle-report/:vin/:isOwner"
              element={<VHR_viewFullReports />}
            />
            <Route
              path="vehiclehistory/full-vehicle-report/:vin"
              element={<VHR_viewFullReports />}
            />
            <Route path="/vehiclehistory/why-buy-vhr" element={<WhyBuyvhr />} />

            <Route
              path="/vehiclehistory/odometer-fraud"
              element={<OdometerFraud />}
            />
            <Route
              path="/vehiclehistory/call-for-action"
              element={<CallForAction />}
            />
            <Route
              path="/vehiclehistory/title-brands"
              element={<TitleBrands />}
            />
            <Route path="/vehiclehistory/faq" element={<Faq />} />
            <Route
              path="/vehiclehistory/buyback-terms"
              element={<BuybackTerms />}
            />
            <Route path="/vehiclehistory/contact" element={<Contact />} />
            <Route path="/vehiclehistory/dispute" element={<Dispute />} />
            <Route
              path="/vehiclehistory/free-online-delivery"
              element={<FreeOnlineDelivery />}
            />
            <Route
              path="/vehiclehistory/secure-shopping"
              element={<SecureShopping />}
            />
            <Route path="/vehiclehistory/glossary" element={<Glossary />} />
            <Route
              path="/vehiclehistory/autocheck-score"
              element={<AutoCheckScore />}
            />
            <Route
              path="/vehiclehistory/patent-notice"
              element={<PatentNotice />}
            />
            <Route
              path="/vehiclehistory/vehicle-buyback-protection"
              element={<VehicleBuybackProtection />}
            />
            <Route
              path="/vehiclehistory/car-gurus"
              element={<CarGurusUser />}
            />

            <Route path="/vehiclehistory/free-score" element={<FreeScore />} />
            <Route
              path="/vehiclehistory/my-vehicle-offers"
              element={<MYVehicleOffers />}
            />
            <Route
              path="/vehiclehistory/preferences-updated"
              element={<PrefrencesUpdated />}
            />
            <Route
              path="/vehiclehistory/recall-check"
              element={<RecallCheck />}
            />
            <Route path="/vehiclehistory/car-advise" element={<CarAdvise />} />

            <Route path="/vehiclehistory/carscom" element={<CarsUser />} />
            <Route
              path="/vehiclehistory/backed-by-experian"
              element={<BackedByExperian />}
            />
            <Route
              path="/vehiclehistory/autocheck-vs-carfax"
              element={<AutoCheckVsCarfax />}
            />
            <Route
              path="/vehiclehistory/autocheck-vs-bargain"
              element={<AutoCheckVsBargain />}
            />
            <Route
              path="/vehiclehistory/flood-risk-check"
              element={<FloodRiskCheck />}
            />
            <Route
              path="/vehiclehistory/affiliate-program"
              element={<AffiliateProgram />}
            />
            <Route path="/vehiclehistory/checkout" element={<Checkout />} />
            <Route
              path="/vehiclehistory/checkout/order-confirmation"
              element={<Checkout />}
            />

            {/* <Route
              path="/vehiclehistory/payment-gateway"
              element={<PaymentGateway />}
            /> */}

            <Route
              path="/vehiclehistory/dashboard"
              element={<MyAutocheckDashboard />}
            />
            <Route
              path="/vehiclehistory/dashboard/my-profile-tab"
              element={<MyProfileTab />}
            />
            <Route
              path="/vehiclehistory/dashboard/my-report-tab"
              element={<MyReportTab />}
            />
            <Route
              path="/vehiclehistory/update-password"
              element={<UpdatePassword />}
            />
            <Route
              path="/vehiclehistory/dashboard/register-for-buyback-protection-tab"
              element={<RegisterBuybackTab />}
            />
            <Route
              path="/vehiclehistory/your-ad-choices"
              element={<YourAdChoices />}
            />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="app_container">
      <Provider store={store}>
        <PreloaderProvider>
          <BrowserRouter>
            <AnalyticsProvider>
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </AnalyticsProvider>
          </BrowserRouter>
        </PreloaderProvider>
      </Provider>
    </div>
  );
}

export default App;
